import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search, BrainCircuit, Clock, History as HistoryIcon } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TYPES = ["fact", "preference", "constraint", "date", "correction", "other"];

function TypeBadge({ t }) {
  return <span className="wr-mono text-[0.6rem] uppercase tracking-wider text-wr-red border border-wr-red/40 px-1.5 py-0.5">{t || "fact"}</span>;
}

export default function Memory() {
  const [view, setView] = useState("active"); // active | history
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [draft, setDraft] = useState("");
  const [draftType, setDraftType] = useState("fact");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/memories", {
        params: { status: view, q: q || undefined, memory_type: typeFilter || undefined },
      });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [view, q, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from the memory-confirmation toast's "Correct" action: open that memory's editor.
  useEffect(() => {
    const cid = searchParams.get("correct");
    if (!cid || loading) return;
    const row = rows.find((r) => r.id === cid);
    if (row) {
      setEditing(cid);
      setEditText(row.content);
      setTimeout(() => {
        document
          .querySelector(`[data-testid="memory-${cid}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    }
    setSearchParams({}, { replace: true });
  }, [rows, loading, searchParams, setSearchParams]);

  const remember = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await api.post("/memories", { content: draft.trim(), memory_type: draftType });
      setDraft(""); toast.success("Sarge will remember that.");
      setView("active"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const forget = async (id) => {
    try { await api.post(`/memories/${id}/forget`); toast.success("Forgotten (history kept)."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/memories/${id}`, { content: editText });
      toast.success("Corrected. Prior version kept in history.");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
      <p className="wr-label !text-wr-red">▸ SARGE · MEMORY</p>
      <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2">What Sarge Knows.</h1>
      <p className="text-sm text-wr-sub mt-2 max-w-xl">
        Facts you state are remembered automatically — no approval needed. Correct or forget anything anytime.
      </p>

      <div className="mt-6 border border-wr-border bg-wr-surface p-4">
        <label className="wr-label">Remember this</label>
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Input data-testid="memory-draft" value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder="A durable fact, preference, or constraint…" onKeyDown={(e) => e.key === "Enter" && remember()}
            className="flex-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
          <select data-testid="memory-draft-type" value={draftType} onChange={(e) => setDraftType(e.target.value)}
            className="bg-wr-bg border border-wr-border text-sm text-white px-2 h-10 wr-mono uppercase">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button onClick={remember} disabled={busy || !draft.trim()} data-testid="memory-add"
            className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider text-xs h-10">
            <Plus size={14} className="mr-1.5" /> Remember
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 border-b border-wr-border">
          {[["active", "Active"], ["history", "History"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`memory-view-${k}`}
              className={`px-4 py-2 wr-mono text-xs uppercase tracking-wider border-b-2 -mb-px ${view === k ? "border-wr-red text-white" : "border-transparent text-wr-sub hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[160px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-wr-muted" />
          <Input data-testid="memory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
            className="pl-9 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 h-9 text-sm" />
        </div>
        <select data-testid="memory-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-wr-bg border border-wr-border text-sm text-white px-2 h-9 wr-mono uppercase">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="mt-4 space-y-3">
        {loading ? <p className="wr-label" data-testid="memory-loading">Loading…</p>
          : rows.length === 0 ? (
            <div className="border border-wr-border bg-wr-surface p-8 text-center" data-testid="memory-empty">
              <BrainCircuit className="mx-auto text-wr-red mb-3" size={24} />
              <p className="text-sm text-wr-sub">{view === "active" ? "Sarge doesn't know anything yet." : "No history."}</p>
            </div>
          ) : rows.map((m) => (
            <div key={m.id} data-testid={`memory-${m.id}`} className="border border-wr-border bg-wr-surface p-4">
              <div className="flex items-start justify-between gap-3">
                {editing === m.id ? (
                  <Input value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`memory-edit-input-${m.id}`}
                    className="flex-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
                ) : (
                  <p className="text-sm text-white leading-relaxed flex-1">{m.content}</p>
                )}
                <TypeBadge t={m.memory_type} />
              </div>
              <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                <span className="wr-mono text-[0.66rem] text-wr-muted flex items-center gap-2">
                  <Clock size={11} /> {m.source_type || "founder"} · {new Date(m.created_at).toLocaleDateString()}
                  {m.status !== "active" && m.status !== "approved" && <span className="uppercase text-wr-red"> · {m.status}</span>}
                </span>
                {view === "active" && (
                  <div className="flex gap-2">
                    {editing === m.id ? (
                      <>
                        <button onClick={() => saveEdit(m.id)} data-testid={`memory-save-${m.id}`}
                          className="px-3 py-1.5 border border-wr-border wr-mono text-[0.66rem] uppercase text-emerald-400 hover:border-emerald-500">Save</button>
                        <button onClick={() => setEditing(null)} className="px-3 py-1.5 border border-wr-border wr-mono text-[0.66rem] uppercase text-wr-sub">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditing(m.id); setEditText(m.content); }} data-testid={`memory-edit-${m.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-wr-border wr-mono text-[0.66rem] uppercase text-wr-sub hover:text-white"><Pencil size={11} /> Correct</button>
                        <button onClick={() => forget(m.id)} data-testid={`memory-forget-${m.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-wr-border wr-mono text-[0.66rem] uppercase text-wr-sub hover:text-wr-red hover:border-wr-red"><Trash2 size={11} /> Forget</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
